import React, {
  MouseEventHandler,
  TouchEvent as ReactTouchEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import classNames from 'classnames'
import { getVariantClasses } from '@/atoms/Button/ButtonUtil'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { GuildIcon } from '@/atoms/Icons/GuildIcon'
import { CaptionMD, HeadingXS, TitleLG } from '@/atoms/Text'
import {
  ANGEL_GUILD_BASIC_MONTHLY_OFFER_ID,
  ANGEL_GUILD_PREMIUM_MONTHLY_OFFER_ID,
  ANGEL_GUILD_PREMIUM_YEARLY_OFFER_ID,
} from '@/constants/offer-ids'
import { useExperimentContext } from '@/experimentation'
import { GuildBenefitList } from '@/molecules/GuildBenefitList'
import { Modal } from '@/molecules/Modal'
import { handleSignUpFlowExperiment } from '@/services/Auth/utils'
import { formatPrice, getPriceForCurrency } from '@/services/PriceFormatter'
import { useUser } from '@/services/UserService'
import { useLocale } from '@/utils/LocaleUtil'
import { useSafeTrack } from '@/utils/analytics'
import { ProductAddedPayload, useEcommerceEvents } from '@/utils/analytics/ecommerce-events'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { AuthStep, SignUpAuthForm } from '@/views/GuildSignUpAuthView/SignUpAuthForm'
import { Tab } from '@/views/GuildSignupView/FrequencySelector/FrequencySelector'
import { FrequencySelector } from '../FrequencySelector'

export interface GuildCtaSheetProps {
  showSheet: boolean
  onClose: () => void
  projectSlug: string
  email?: string | null
  target?: '_blank'
  guid: string
}

const getPlanId = (tab: Tab) => {
  if (tab === 'monthly-premium') return ANGEL_GUILD_PREMIUM_MONTHLY_OFFER_ID
  if (tab === 'yearly-premium') return ANGEL_GUILD_PREMIUM_YEARLY_OFFER_ID
  return ANGEL_GUILD_BASIC_MONTHLY_OFFER_ID
}

export const GuildCtaSheet: React.FC<GuildCtaSheetProps> = ({ showSheet = false, onClose, projectSlug, guid }) => {
  const [step, setStep] = useState<AuthStep>('email')
  const [showAuthModal, setShowAuthModal] = useState(false)
  const { locale } = useLocale()
  const [isVisible, setIsVisible] = useState(false)
  const [tabPosition, setTabPosition] = useState<Tab>('monthly-premium')
  const [touchStart, setTouchStart] = useState(0)
  const [touchEnd, setTouchEnd] = useState(0)
  const { t } = useTranslate('livestream')
  const ref = useRef<HTMLDivElement | null>(null)
  const track = useSafeTrack()
  const { trackProductViewed, trackProductAdded } = useEcommerceEvents()
  const { getFeatureValue } = useExperimentContext()
  const router = useSafeRouter()
  const { isLoggedIn } = useUser()

  const minSwipeDistance = 250
  const planId = getPlanId(tabPosition)
  const queryString = `?utm_campaign=guild-memberships&utm_source=livestream&utm-medium=web&utm_term=${projectSlug}&utm_content=${guid}`

  const handleFrequencySelection = (tab: Tab) => {
    setTabPosition(tab)
  }

  const price = tabPosition === 'yearly-premium' ? 17900 : tabPosition === 'monthly-premium' ? 2000 : 1200
  const paymentValue = useMemo(() => {
    return formatPrice(price, {
      locale,
      currency: 'USD',
    })
  }, [locale, price])

  const handleGuildSignupStarted: MouseEventHandler<HTMLAnchorElement> = useCallback(
    (e) => {
      // TODO: deprecated - remove after release of trackProductAdded and marketing's (Kylo's) blessing
      track('Guild Checkout CTA Clicked', {
        context: 'Guild Membership Early Access Page Checkout Started',
        slug: projectSlug,
        subscriptionType: tabPosition,
        planId,
      })
      track('Guild Checkout Started', {
        planId,
      })
      const trackProductAddedData: ProductAddedPayload = {
        funnel: 'guild',
        category: 'guild',
        name: tabPosition,
        price: getPriceForCurrency(price, 'USD'),
        currency: 'USD',
        quantity: 1,
        product_id: planId,
        sku: planId,
        projectSlug,
      }

      if (isLoggedIn) {
        trackProductAdded({ ...trackProductAddedData, url: `/guild/checkout/${planId}${queryString}` })
        return
      }

      const signUpFlowFeatureValue = getFeatureValue('new-guild-sign-up-flow-v2', 'control')
      const isControl = signUpFlowFeatureValue === 'control'
      const guildSignupLink = `/guild/${isControl ? 'checkout' : 'signup'}/${planId}${queryString}`
      trackProductAdded({
        ...trackProductAddedData,
        url: guildSignupLink,
      })
      const handleOpenAuthModal = () => {
        setIsVisible(false)
        setTimeout(() => setShowAuthModal(true), 200)
      }
      handleSignUpFlowExperiment(e, guildSignupLink, router, signUpFlowFeatureValue, handleOpenAuthModal)
    },
    [
      price,
      track,
      trackProductAdded,
      projectSlug,
      planId,
      tabPosition,
      getFeatureValue,
      router,
      queryString,
      isLoggedIn,
    ],
  )

  const handleClickOutside = useCallback(
    (event: MouseEvent | TouchEvent) => {
      if (showSheet && ref.current && !ref.current.contains(event.target as Node)) {
        onClose()
      }
    },
    [ref, onClose, showSheet],
  )

  const onTouchStart = (event: ReactTouchEvent) => {
    setTouchEnd(0)
    setTouchStart(event.targetTouches[0].clientY)
  }

  const onTouchMove = (event: ReactTouchEvent) => setTouchEnd(event.targetTouches[0].clientY)

  const onTouchEnd = useCallback(() => {
    if (!touchStart || !touchEnd) return
    const html = window.document.getElementsByTagName('html')[0]
    html.style.overflow = 'hidden'
    const distance = touchStart - touchEnd
    const isSwipeDown = distance < -minSwipeDistance
    if (showSheet && isSwipeDown) {
      onClose()
      html.style.overflow = 'auto'
    }
  }, [onClose, showSheet, touchStart, touchEnd])

  useEffect(() => {
    if (showSheet) {
      setIsVisible(true)
    } else {
      setIsVisible(false)
    }
  }, [showSheet])

  useEffect(() => {
    if (!isVisible) return

    trackProductViewed({
      funnel: 'guild',
      category: 'guild',
      name: tabPosition,
      price: getPriceForCurrency(price, 'USD'),
      currency: 'USD',
      quantity: 1,
      product_id: planId || '',
      sku: planId || '',
      projectSlug,
    })
  }, [isVisible, planId, price, projectSlug, tabPosition, trackProductViewed])

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('touchstart', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('touchstart', handleClickOutside)
    }
  }, [handleClickOutside])

  const paymentInterval = tabPosition === 'yearly-premium' ? t('yearly', 'Yearly') : t('monthly', 'Monthly')

  return (
    <>
      {isVisible && (
        <div
          className={classNames(
            'fixed inset-0 z-[1999]  backdrop-opacity-70 transition-all',
            isVisible ? 'bg-core-gray-950/70' : 'bg-transparent',
          )}
        />
      )}
      <div
        ref={ref}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        className={classNames(
          'box-border fixed bottom-0 left-0 z-[2000] max-h-[90vh] w-full rounded-t-3xl border-t bg-core-gray-100 transition-all duration-300 ease-in-out',
          'sm:max-w-[400px] sm:right-0 sm:mx-auto sm:rounded-b-3xl sm:overflow-hidden sm:h-fit',
          isVisible ? 'translate-y-0 sm:my-auto sm:bottom-0 sm:top-0 sm:opacity-1' : 'translate-y-full sm:opacity-0',
        )}
      >
        <section className="px-4 pt-4">
          <div className="flex items-center justify-center">
            <div className="-mt-2 h-1 w-12 rounded-3xl bg-gray-300" />
          </div>
          <div className="flex justify-end">
            <div className="rounded-full bg-gray-200 p-1.5" onClick={onClose}>
              <CloseIcon size={18} color={'gray-700'} />
            </div>
          </div>
          <div className="flex flex-col items-center text-center">
            <GuildIcon size={48} color1={'core-gray-950'} />
            <HeadingXS className="w-[80%] !font-bold">Angel Guild Membership</HeadingXS>
          </div>
          <div className="mx-auto max-w-[600px]">
            <FrequencySelector tabPosition={tabPosition} onClick={handleFrequencySelection} />
            <GuildBenefitList
              tier={tabPosition}
              className="mx-4 my-6 max-h-[calc(90vh-488px)] overflow-y-scroll px-2 md:px-2 md:scrollbar-thin md:scrollbar-track-gray-100 md:scrollbar-thumb-core-gray-200"
            />
          </div>
        </section>
        <section className="z-41 flex min-h-[144px] justify-center bg-gray-100 px-6 pb-8 pt-3 shadow-[0_-8px_16px_-1px_rgba(0,0,0,0.08)]">
          <div className="flex w-full flex-col sm:max-w-[600px] sm:px-2">
            <div className="mb-2 flex w-full flex-row items-center justify-between">
              <CaptionMD className="capitalize">
                <Translate i18nKey="intervalPayment" t={t} values={{ paymentInterval }}>
                  {{ paymentInterval }} Payment:
                </Translate>
              </CaptionMD>
              <TitleLG weight={'bold'}>{paymentValue}</TitleLG>
            </div>
            <a
              className={classNames(
                'btn btn-standard',
                getVariantClasses({ variant: 'oxide-core' }),
                'mb-3 w-full rounded-xl py-3.5',
              )}
              href={`/guild/checkout/${planId}${queryString}`}
              onClick={handleGuildSignupStarted}
            >
              <Translate t={t} i18nKey="joinTheGuild">
                Join the Guild
              </Translate>
            </a>
          </div>
        </section>
        {planId && (
          <Modal onClose={() => setShowAuthModal(false)} isOpen={showAuthModal}>
            <SignUpAuthForm
              isModal
              offerId={planId}
              userEmail=""
              onEmailSubmit={() => setStep('create-account')}
              step={step}
            />
          </Modal>
        )}
      </div>
    </>
  )
}
